import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import type { OrgBillingSettings } from '@/shared/types/billing';

/**
 * Organization billing settings — currency, invoice/receipt formats, and
 * the canonical Billing Group list shown in the service add/edit row.
 *
 * Endpoint: `GET /api/Billing/Settings/{organizationId}` — mirrors Flutter
 * `settings_data_source_impl.dart:1276 fetchBillingSettings`.
 *
 * Items in `billingGroups` carry their server `id` / `system` /
 * `organizationId`; the create/update service payload needs the full
 * object on the wire (Flutter `BillingGroupPopup.onChanged` passes the
 * whole entry through, not just the category name).
 *
 * Settings are organization-scoped and rarely change — cache for the
 * whole session.
 */
export function useBillingSettings(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['billing', 'settings', organizationId],
    queryFn: ({ signal }) =>
      get<OrgBillingSettings>(`${BILLING_BASE}/Settings/${organizationId}`, {
        signal,
      }),
    enabled: Boolean(organizationId),
    staleTime: Infinity,
  });
}
